import { Chip, Stack, Tooltip } from '@mui/material';
import { RouterLink } from 'common/ui/RouterLink';
import { CandidateProfileListProps } from 'modules/candidate-profile/types/CandidateProfileListProps';
import { getCandidateProfileLinkProps } from 'modules/candidate-profile/utils/getCandidateProfileLink';
import { CandidateInviteStatus } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  CANDIDATE_PROFILE_TABLE_ID,
  CANDIDATE_PROFILE_TABLE_SCROLL_IGNORED_PARAMS
} from 'modules/candidate-profile/constants';
import { saveTableScrollPosition } from 'common/table/hooks/useTableScrollRestoration';

type CandidateProfileNameCellProps = {
  candidateProfileListProps: CandidateProfileListProps;
  id: string;
  name: string;
  inviteStatus?: CandidateInviteStatus | null;
};

const getInviteStatusLabel = (inviteStatus: CandidateInviteStatus): string => {
  switch (inviteStatus) {
    case CandidateInviteStatus.APPLIED:
      return 'Already applied';
    case CandidateInviteStatus.INVITED:
      return 'Already invited';
    case CandidateInviteStatus.IN_LIST:
      return 'Already in list';
  }
};

const getInviteStatusTooltip = (
  inviteStatus: CandidateInviteStatus
): string => {
  switch (inviteStatus) {
    case CandidateInviteStatus.APPLIED:
    case CandidateInviteStatus.INVITED:
      return 'This candidate has already been invited or applied to this analysis';
    case CandidateInviteStatus.IN_LIST:
      return 'This candidate is already in this talent list';
  }
};

export const CandidateProfileNameCell: React.FC<
  CandidateProfileNameCellProps
> = ({ candidateProfileListProps, id, name, inviteStatus }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const currentPath = location.pathname + location.search;

  const linkProps = getCandidateProfileLinkProps({
    candidateProfileListProps,
    id,
    searchParams,
    currentPath
  });

  // Save scroll position before navigating to candidate detail.
  // This enables scroll restoration when the user navigates back.
  // The position is keyed by the full URL (including filters/pagination) so
  // restoration only happens when returning to the exact same view.
  // See useTableScrollRestoration hook for the restoration logic.
  const handleClick = () => {
    saveTableScrollPosition(
      CANDIDATE_PROFILE_TABLE_ID,
      location.pathname + location.search,
      CANDIDATE_PROFILE_TABLE_SCROLL_IGNORED_PARAMS
    );
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <RouterLink
        to={linkProps.to}
        state={linkProps.state}
        onClick={handleClick}
      >
        {name}
      </RouterLink>
      {inviteStatus && (
        <Tooltip
          title={getInviteStatusTooltip(inviteStatus)}
          placement="top"
          arrow
        >
          <Chip
            label={getInviteStatusLabel(inviteStatus)}
            size="small"
            color="error"
          />
        </Tooltip>
      )}
    </Stack>
  );
};
